import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript,
  InfoWindow,
  Polygon,
} from "@react-google-maps/api";
import "./map.css";
import { backend_url, googleMapsApiKey } from "../App";
import { FaFire, FaSmog, FaCamera } from "react-icons/fa";

const MiniMap = memo(function MiniMap(props) {
  const { camId, selectedEvent } = props;

  const [circleCenter, setCircleCenter] = useState({
    lat: 34.534508,
    lng: 73.003801,
  });

  const [cones, setCones] = useState([]);
  const [hoveredCone, setHoveredCone] = useState(null);
  const [loadingCone, setLoadingCone] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");
  const [currentEventId, setCurrentEventId] = useState(null);

  const fetchingRef = useRef(false);
  const cameraDataRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  const calculateConeAtAngle = useCallback(
    (position, angleDegrees, fovDegrees = 25, rangeMeters = 800) => {
      if (
        !position ||
        position.lat === undefined ||
        position.lng === undefined
      ) {
        return null;
      }

      const { lat, lng } = position;
      const directionRad = ((angleDegrees - 90) * Math.PI) / 180;
      const halfFovRad = ((fovDegrees / 2) * Math.PI) / 180;

      const leftAngleRad = directionRad - halfFovRad;
      const rightAngleRad = directionRad + halfFovRad;

      const rangeLat = rangeMeters / 111320;
      const rangeLng = rangeMeters / (111320 * Math.cos((lat * Math.PI) / 180));

      const leftPoint = {
        lat: lat + Math.sin(leftAngleRad) * rangeLat,
        lng: lng + Math.cos(leftAngleRad) * rangeLng,
      };

      const rightPoint = {
        lat: lat + Math.sin(rightAngleRad) * rangeLat,
        lng: lng + Math.cos(rightAngleRad) * rangeLng,
      };

      const centerPoint = {
        lat: lat + Math.sin(directionRad) * rangeLat,
        lng: lng + Math.cos(directionRad) * rangeLng,
      };

      return {
        conePath: [{ lat, lng }, leftPoint, rightPoint, { lat, lng }],
        centerPoint,
      };
    },
    [],
  );

  // Fetch camera details once
  useEffect(() => {
    const fetchCameraDetails = async () => {
      if (!camId || cameraDataRef.current) return;

      try {
        console.log("Fetching camera details for ID:", camId);
        const response = await fetch(
          `${backend_url}/core/api/camera/${camId}/`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch camera");
        const camera = await response.json();
        console.log("Camera data:", {
          start_angle: camera.start_angle,
          end_angle: camera.end_angle,
          video_interval: camera.video_interval,
          description: camera.description,
        });
        cameraDataRef.current = camera;
        setCircleCenter({ lat: camera.latitude, lng: camera.longitude });
      } catch (error) {
        console.error("Error fetching camera:", error);
      }
    };

    fetchCameraDetails();
  }, [camId]);

  // Reset cones when selectedEvent changes
  useEffect(() => {
    if (selectedEvent?.uuid !== currentEventId) {
      console.log("Resetting cones for new event:", selectedEvent?.uuid);
      setCones([]);
      setHoveredCone(null);
      setDebugInfo("");
      setCurrentEventId(selectedEvent?.uuid || null);
      fetchingRef.current = false;
    }
  }, [selectedEvent?.uuid, currentEventId]);

  // Main logic - Fetch images and their bounding boxes
  useEffect(() => {
    // Add null check for selectedEvent
    if (!selectedEvent) {
      console.log("No selected event");
      return;
    }

    if (fetchingRef.current) return;
    if (!selectedEvent?.uuid || !camId || !cameraDataRef.current) return;
    if (currentEventId !== selectedEvent.uuid) return;

    fetchingRef.current = true;

    const fetchAndCalculateCones = async () => {
      setLoadingCone(true);
      setDebugInfo("Finding fire/smoke detections...");

      try {
        const camera = cameraDataRef.current;
        const startAngle = camera.start_angle || 0;
        const endAngle = camera.end_angle || 180;
        const totalFrames = camera.video_interval || 15;

        // Get event species to know what was detected - with null check
        const eventSpecies = selectedEvent.species || [];
        const hasFireInEvent = eventSpecies.some(
          (s) => s?.id === "fire" || s?.name?.toLowerCase() === "fire",
        );
        const hasSmokeInEvent = eventSpecies.some(
          (s) => s?.id === "smoke" || s?.name?.toLowerCase() === "smoke",
        );

        console.log(
          `Event ${selectedEvent.uuid}: Fire=${hasFireInEvent}, Smoke=${hasSmokeInEvent}`,
        );

        // If event has no fire or smoke, don't show any cones
        if (!hasFireInEvent && !hasSmokeInEvent) {
          setDebugInfo("No fire/smoke in this event");
          setLoadingCone(false);
          fetchingRef.current = false;
          return;
        }

        // Get all images for this event
        const imagesResponse = await fetch(
          `${backend_url}/core/api/image/?event=${selectedEvent.uuid}&page_size=100`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        const imagesData = await imagesResponse.json();
        const eventImages = imagesData.results || [];

        console.log(`Found ${eventImages.length} images in this event`);

        if (eventImages.length === 0) {
          setDebugInfo("No images found");
          setLoadingCone(false);
          fetchingRef.current = false;
          return;
        }

        // For each image, fetch its bounding boxes using the backend filter
        const imagesWithDetections = [];

        for (let i = 0; i < eventImages.length; i++) {
          const image = eventImages[i];

          try {
            // Use the backend filter to get boxes only for this specific image
            const boxesResponse = await fetch(
              `${backend_url}/core/api/box/?image=${image.id}`,
              {
                headers: {
                  Authorization: `Token ${localStorage.getItem("token")}`,
                },
              },
            );
            const boxesData = await boxesResponse.json();
            const boxes = boxesData.results || boxesData;

            let hasFire = false;
            let hasSmoke = false;
            let fireCount = 0;
            let smokeCount = 0;

            for (const box of boxes) {
              const specieId = box.specie?.id || box.specie;
              const specieName = box.specie?.name || "";

              if (specieId === "fire" || specieName?.toLowerCase() === "fire") {
                hasFire = true;
                fireCount++;
              }
              if (
                specieId === "smoke" ||
                specieName?.toLowerCase() === "smoke"
              ) {
                hasSmoke = true;
                smokeCount++;
              }
            }

            // Only add if there's at least one fire or smoke detection
            if ((hasFire && hasFireInEvent) || (hasSmoke && hasSmokeInEvent)) {
              const species = hasFire ? "fire" : "smoke";
              console.log(
                `✅ Image ${i} (ID: ${image.id}) - Fire:${fireCount} Smoke:${smokeCount}`,
              );
              imagesWithDetections.push({
                imageId: image.id,
                imageUrl: image.file,
                imageDate: image.date,
                specie: species,
                frameIndex: i,
                hasFire,
                hasSmoke,
                fireCount,
                smokeCount,
              });
            } else {
              console.log(
                `❌ Image ${i} (ID: ${image.id}) - No fire/smoke detections`,
              );
            }
          } catch (boxError) {
            console.error(
              `Error fetching boxes for image ${image.id}:`,
              boxError,
            );
          }
        }

        console.log(
          `Found ${imagesWithDetections.length} images with fire/smoke detections`,
        );

        if (imagesWithDetections.length === 0) {
          setDebugInfo("No fire/smoke detections found in this event");
          setLoadingCone(false);
          fetchingRef.current = false;
          return;
        }

        // Calculate cone for each detection
        const newCones = [];

        for (const detection of imagesWithDetections) {
          // Calculate angle based on frame position in the pan cycle
          const positionInCycle = detection.frameIndex % totalFrames;
          const angleStep = (endAngle - startAngle) / (totalFrames - 1);
          const angle = startAngle + angleStep * positionInCycle;

          console.log(
            `Frame ${detection.frameIndex} → Cycle ${positionInCycle}/${totalFrames} → ${Math.round(angle)}° (${detection.specie})`,
          );

          const coneResult = calculateConeAtAngle(
            { lat: camera.latitude, lng: camera.longitude },
            angle,
            25,
            800,
          );

          if (coneResult) {
            newCones.push({
              id: `${selectedEvent.uuid}-${detection.imageId}`,
              conePath: coneResult.conePath,
              centerPoint: coneResult.centerPoint,
              angle: angle,
              specie: detection.specie,
              imageUrl: detection.imageUrl,
              imageDate: detection.imageDate,
              frameIndex: detection.frameIndex,
              hasFire: detection.hasFire,
              hasSmoke: detection.hasSmoke,
              fireCount: detection.fireCount,
              smokeCount: detection.smokeCount,
            });
          }
        }

        console.log(`======= TOTAL CONES CREATED: ${newCones.length} =======`);
        setCones(newCones);

        if (newCones.length > 0) {
          setDebugInfo(
            `✅ ${newCones.length} cone(s) for fire/smoke detection(s)`,
          );
        } else {
          setDebugInfo("No cones could be created");
        }
      } catch (error) {
        console.error("Error:", error);
        setDebugInfo(`Error: ${error.message}`);
      } finally {
        setLoadingCone(false);
        fetchingRef.current = false;
      }
    };

    fetchAndCalculateCones();
  }, [
    selectedEvent?.uuid,
    camId,
    currentEventId,
    calculateConeAtAngle,
    selectedEvent,
  ]);

  const getConeColor = (specie) => {
    if (specie === "fire") return "#FF0000";
    if (specie === "smoke") return "#FF6600";
    return "#00FF00";
  };

  const getConeIcon = (specie) => {
    if (specie === "fire") return <FaFire size={12} />;
    if (specie === "smoke") return <FaSmog size={12} />;
    return <FaCamera size={12} />;
  };

  const getConeTitle = (specie) => {
    if (specie === "fire") return "🔥 FIRE DETECTED";
    if (specie === "smoke") return "💨 SMOKE DETECTED";
    return "📷 CAMERA DIRECTION";
  };

  const getConeGradient = (specie) => {
    if (specie === "fire")
      return "linear-gradient(135deg, #FF0000 0%, #CC0000 100%)";
    if (specie === "smoke")
      return "linear-gradient(135deg, #FF6600 0%, #CC4400 100%)";
    return "linear-gradient(135deg, #00AA00 0%, #008800 100%)";
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getConeCenter = (conePath, defaultCenter) => {
    if (conePath && conePath.length >= 3) {
      return {
        lat: (conePath[1].lat + conePath[2].lat) / 2,
        lng: (conePath[1].lng + conePath[2].lng) / 2,
      };
    }
    return defaultCenter;
  };

  if (loadError)
    return <div style={{ fontSize: "10px", padding: "5px" }}>Map error</div>;
  if (!isLoaded)
    return (
      <div style={{ fontSize: "10px", padding: "5px" }}>Loading map...</div>
    );

  return (
    <GoogleMap
      onLoad={() => {}}
      center={circleCenter}
      zoom={12.7}
      mapContainerStyle={{
        height: "600px",
        width: "355px",
        borderRadius: "12px",
        // margin: "1px",
      }}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {/* Debug indicator */}
      {(loadingCone || debugInfo) && (
        <div
          style={{
            position: "absolute",
            top: "5px",
            left: "5px",
            background: "rgba(0,0,0,0.8)",
            color: cones.length > 0 ? "#00ff00" : "#ffaa00",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "9px",
            zIndex: 1000,
            fontFamily: "monospace",
            maxWidth: "95%",
          }}
        >
          {loadingCone ? "Loading..." : debugInfo}
          {cones.length > 0 &&
            ` (${cones.length} cone${cones.length !== 1 ? "s" : ""})`}
        </div>
      )}

      {/* Camera Marker */}
      <Marker
        position={circleCenter}
        icon={{
          url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
          scaledSize: new window.google.maps.Size(25, 25),
          anchor: new window.google.maps.Point(12, 12),
        }}
      />

      {/* Circle around camera */}
      <Circle
        center={circleCenter}
        radius={3000}
        options={{
          fillColor: "#FF0000",
          fillOpacity: 0.05,
          strokeColor: "#FF0000",
          strokeOpacity: 0.5,
          strokeWeight: 1,
        }}
      />

      {/* Cones - only for actual fire/smoke detections */}
      {cones.map((cone) => (
        <React.Fragment key={cone.id}>
          <Polygon
            path={cone.conePath}
            options={{
              fillColor: getConeColor(cone.specie),
              fillOpacity: 0.6,
              strokeColor: getConeColor(cone.specie),
              strokeOpacity: 0.9,
              strokeWeight: 1.5,
              geodesic: true,
            }}
            onMouseOver={() => setHoveredCone(cone.id)}
            onMouseOut={() => setHoveredCone(null)}
          />

          {hoveredCone === cone.id && (
            <InfoWindow
              position={
                cone.centerPoint || getConeCenter(cone.conePath, circleCenter)
              }
              options={{
                pixelOffset: new window.google.maps.Size(0, -5),
                disableAutoPan: true,
                maxWidth: 280,
              }}
            >
              <div
                style={{
                  background: getConeGradient(cone.specie),
                  color: "white",
                  borderRadius: "8px",
                  padding: "10px",
                  fontSize: "11px",
                  minWidth: "200px",
                  maxWidth: "260px",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    borderBottom: "1px solid rgba(255,255,255,0.3)",
                    paddingBottom: "5px",
                  }}
                >
                  {getConeIcon(cone.specie)}
                  <strong style={{ fontSize: "12px" }}>
                    {cone.hasFire && cone.hasSmoke
                      ? "🔥 FIRE & 💨 SMOKE"
                      : getConeTitle(cone.specie)}
                  </strong>
                </div>

                {/* Camera Name */}
                <div style={{ marginBottom: "5px", fontSize: "10px" }}>
                  📷 <strong>Camera:</strong>{" "}
                  {cameraDataRef.current?.description || `Camera ${camId}`}
                </div>

                {/* Direction Angle */}
                <div style={{ marginBottom: "5px", fontSize: "10px" }}>
                  📐 <strong>Direction:</strong> {Math.round(cone.angle)}°
                </div>

                {/* Frame Number */}
                <div style={{ marginBottom: "5px", fontSize: "10px" }}>
                  🎬 <strong>Frame:</strong> {cone.frameIndex + 1}/
                  {cameraDataRef.current?.video_interval || 15}
                </div>

                {/* Detection Counts */}
                <div style={{ marginBottom: "5px", fontSize: "10px" }}>
                  🔥 <strong>Fire boxes:</strong> {cone.fireCount || 0} | 💨{" "}
                  <strong>Smoke boxes:</strong> {cone.smokeCount || 0}
                </div>

                {/* Date & Time */}
                <div style={{ marginBottom: "8px", fontSize: "10px" }}>
                  🕐 <strong>Detection Time:</strong>{" "}
                  {formatDateTime(cone.imageDate)}
                </div>

                {/* Detection Image Preview */}
                {cone.imageUrl && (
                  <div
                    style={{
                      marginTop: "8px",
                      borderTop: "1px solid rgba(255,255,255,0.3)",
                      paddingTop: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "9px",
                        marginBottom: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      📸 Detection Image:
                    </div>
                    <img
                      src={cone.imageUrl}
                      alt="Detection"
                      style={{
                        width: "100%",
                        maxHeight: "120px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        border: "1px solid rgba(255,255,255,0.5)",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}

                {/* Unique Cone ID */}
                <div
                  style={{
                    fontSize: "8px",
                    opacity: 0.6,
                    marginTop: "8px",
                    textAlign: "center",
                    borderTop: "1px solid rgba(255,255,255,0.2)",
                    paddingTop: "4px",
                  }}
                >
                  🆔 {cone.id.slice(-12)}
                </div>
              </div>
            </InfoWindow>
          )}
        </React.Fragment>
      ))}
    </GoogleMap>
  );
});

export default MiniMap;
